import React, { useState, useCallback, useRef, useEffect } from 'react';
import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import LazyImage from '../shared/LazyImage';
import VideoCarousel from '../shared/VideoCarousel';
import { getQuizOptionsData } from '../../constants';
import { withFirstFramePoster } from '../../utils/video';
import { loadCharacterAttributes, getAttributeId as getAttrId } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackStep2 } from '../../utils/analytics';
import { useDesign } from '../../contexts/DesignContext';
import { useVideoAnimation } from '../../hooks/useVideoAnimation';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import iconHearts from '../../assets/valentinesDay/iconHearts.png';
import iconHeart from '../../assets/valentinesDay/iconHeart.png';

const Step1 = ({ onAnswer, onPrevious, selections, isTransitioning }) => {
  const { isNewDesign } = useDesign();
  const {
    animationMode,
    isMobile,
    shouldPlayOnHoverOrSelected,
    shouldShowVideos,
    getVideoProps,
  } = useVideoAnimation();
  const location = useLocation();

  const { attributeSelectionStyle } = useGrowthBookValues();
  const selectedStyle = location.pathname.includes('/create-ai-hentai')
    ? 'Anime'
    : location.pathname.includes('/create-ai-porn')
    ? 'Realistic'
    : selections?.welcome || 'Realistic';

  const ethnicities = getQuizOptionsData('ethnicity', selectedStyle);
  const [selectedEthnicity, setSelectedEthnicity] = useState(
    ethnicities[0]?.value || '',
  );
  const [selectedAge, setSelectedAge] = useState(22);
  const [videoErrors, setVideoErrors] = useState({});

  const sliderRef = useRef();
  const isHalloween = !!location.pathname.includes('/halloween');
  const isValentines = !!location.pathname.includes('/valentines');

  useEffect(() => {
    setVideoErrors({});
  }, [selectedStyle]);

  useEffect(() => {
    loadCharacterAttributes();
  }, []);

  const updateSliderBackground = (target, value) => {
    const min = Number(target.min);
    const max = Number(target.max);
    const percent = ((value - min) / (max - min)) * 100;

    if (isHalloween) {
      target.style.backgroundRepeat = 'no-repeat';
      target.style.backgroundImage = `linear-gradient(100.44deg, #ED712B 0%, #D03C3C ${percent}%, rgba(255,255,255,0.05) ${percent}%)`;
      target.style.backgroundSize = '100% 100%';
      return;
    }

    target.style.backgroundRepeat = 'no-repeat, no-repeat';
    target.style.backgroundImage = `repeating-linear-gradient(-42deg, #b39ddb 0px, #b39ddb 8px, #8b5cf6 12px, #8b5cf6 18px, #b39ddb 22px), linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.05) 100%)`;
    target.style.backgroundSize = `${percent}% 100%, 100% 100%`;
  };

  useEffect(() => {
    if (!sliderRef.current) return;
    updateSliderBackground(sliderRef.current, selectedAge);
  }, [selectedAge, isHalloween, isValentines, isNewDesign]);

  const canContinue = isNewDesign ? selectedEthnicity && selectedAge : selectedEthnicity;

  const mapAgeToApiLabel = (age) => {
    if (age >= 18 && age <= 19) return '18+';
    if (age >= 20 && age <= 29) return '20s';
    if (age >= 30 && age <= 39) return '30s';
    return '40+';
  };

  const getAttributeId = (type, label) => {
    if (type === 'age') label = mapAgeToApiLabel(label);
    return getAttrId(type, label, selectedStyle, 'Girls');
  };

  const videoRefs = useRef({});

  // Update video sources imperatively to avoid reloading
  useEffect(() => {
    ethnicities.forEach((ethnicity) => {
      // Update extendVideo
      const extendVideo = videoRefs.current[`${ethnicity.value}_extend`];
      if (extendVideo && ethnicity.extendVideo) {
        const targetSrc = withFirstFramePoster(ethnicity.extendVideo);
        const currentSrc = extendVideo.src;
        const newSrc = targetSrc.startsWith('http')
          ? targetSrc
          : new URL(targetSrc, window.location.origin).href;

        if (currentSrc !== newSrc) {
          extendVideo.src = targetSrc;
        }
      }

      // Update regular video
      const regularVideo = videoRefs.current[ethnicity.value];
      if (regularVideo && ethnicity.video) {
        const targetSrc = withFirstFramePoster(ethnicity.video);
        const currentSrc = regularVideo.src;
        const newSrc = targetSrc.startsWith('http')
          ? targetSrc
          : new URL(targetSrc, window.location.origin).href;

        if (currentSrc !== newSrc) {
          regularVideo.src = targetSrc;
        }
      }
    });
  }, [ethnicities, selectedEthnicity]);

  const handleEthnicityClick = useCallback(
    (value) => {
      if (isTransitioning) return;

      setSelectedEthnicity(value);
      const id = getAttributeId('ethnicity', value);
      console.log('📝 Ethnicity answer:', value);
      console.log('🔗 API ID:', id);
    },
    [isTransitioning, selectedStyle],
  );

  // Воспроизводим видео выбранной опции в режиме MOUSEOVER (на десктопе и мобилках)
  useEffect(() => {
    if (!shouldPlayOnHoverOrSelected) return;

    const selectedEthnicityData = ethnicities.find(
      (eth) => eth.value === selectedEthnicity,
    );

    if (!selectedEthnicityData) return;

    // Останавливаем все видео
    Object.values(videoRefs.current).forEach((video) => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0;
      }
    });

    // Запускаем видео выбранной опции
    const selectedVideo =
      videoRefs.current[`${selectedEthnicity}_extend`] ||
      videoRefs.current[selectedEthnicity];

    if (selectedVideo && selectedVideo.paused) {
      setTimeout(() => {
        selectedVideo.currentTime = 0;
        selectedVideo.play().catch(console.warn);
      }, 100);
    }
  }, [selectedEthnicity, shouldPlayOnHoverOrSelected, ethnicities]);

  const handleAgeChange = useCallback(
    (event) => {
      if (isTransitioning) return;

      const target = event.target;
      const value = Number(target.value);

      setSelectedAge(value);
      updateSliderBackground(target, value);

    },
    [isTransitioning],
  );
  const handleContinue = useCallback(() => {
    if (isTransitioning || !canContinue) return;

    const ethnicityId = getAttributeId('ethnicity', selectedEthnicity);
    const ageId = getAttributeId('age', selectedAge);

    console.log('Continue clicked with:', {
      ethnicity: selectedEthnicity,
      ethnicityId,
      age: selectedAge,
      ageId,
    });

    trackStep2(selectedAge, selectedEthnicity);

    onAnswer('step1', {
      ethnicity: selectedEthnicity,
      age: selectedAge,
    });
  }, [selectedEthnicity, selectedAge, selectedStyle, onAnswer, isTransitioning]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning) return;
    onPrevious();
  }, [onPrevious, isTransitioning]);

  const { t } = useTranslation();

  const useCarousel = isNewDesign && attributeSelectionStyle === 'CARROUSEL';

  const carouselEthnicities = ethnicities.map((eth) => ({
    ...eth,
    video: eth.extendVideo,
    value: eth.value,
    title: eth.text,
  }));

  const handleVideoMouseEnter = useCallback(
    (e, isSelected) => {
      if (shouldPlayOnHoverOrSelected && !isMobile && e.target.paused) {
        e.target.currentTime = 0;
        e.target.play().catch(console.warn);
      }
    },
    [shouldPlayOnHoverOrSelected, isMobile],
  );

  const handleVideoMouseLeave = useCallback(
    (e, isSelected) => {
      // Не останавливаем видео если опция выбрана
      if (
        shouldPlayOnHoverOrSelected &&
        !isMobile &&
        !e.target.paused &&
        !isSelected
      ) {
        e.target.pause();
        e.target.currentTime = 0;
      }
    },
    [shouldPlayOnHoverOrSelected, isMobile],
  );

  const handleVideoClick = useCallback(
    (e) => {
      if (animationMode === 'MOUSEOVER' && isMobile) {
        if (e.target.paused) {
          e.target.currentTime = 0;
          e.target.play().catch(console.warn);
        } else {
          e.target.pause();
          e.target.currentTime = 0;
        }
      }
    },
    [animationMode, isMobile],
  );

  const renderMedia = (ethnicity, isActive) => {
    const shouldShowVideo =
      shouldShowVideos &&
      (selectedStyle === 'Realistic' || selectedStyle === 'Anime');

    if (isMobile) {
      const videoSrc = ethnicity.extendVideo;
      const videoKey = `${ethnicity.value}_extend`;
      const hasError = videoErrors[videoKey];
      if (!shouldShowVideo || !videoSrc || hasError) {
        return (
          <>
            <LazyImage
              src={ethnicity.leftGirl}
              className={`leftGirlStep1 ${isActive ? 'active' : ''}`}
              alt={ethnicity.alt}
            />
            <LazyImage
              src={ethnicity.image}
              className={`centeredGirlStep1 ${isActive ? 'active' : ''}`}
              alt={ethnicity.alt}
            />
            <LazyImage
              src={ethnicity.rightGirl}
              className={`rightGirlStep1 ${isActive ? 'active' : ''}`}
              alt={ethnicity.alt}
            />
          </>
        );
      }

      return (
        <video
          ref={(el) => (videoRefs.current[videoKey] = el)}
          key={videoKey}
          className={`mobileFullVideo ${isActive ? 'active' : ''}`}
          src={withFirstFramePoster(videoSrc)}
          {...getVideoProps()}
          preload="auto"
          // poster={ethnicity.image}
          onMouseEnter={(e) => handleVideoMouseEnter(e, isActive)}
          onMouseLeave={(e) => handleVideoMouseLeave(e, isActive)}
          onClick={handleVideoClick}
          onError={() => {
            setVideoErrors((prev) => ({ ...prev, [videoKey]: true }));
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      );
    }

    const videoSrc = isActive ? ethnicity.extendVideo : ethnicity.video;

    const videoKey = isActive ? `${ethnicity.value}_extend` : ethnicity.value;
    const hasError = videoErrors[videoKey];

    if (!shouldShowVideo || !videoSrc || hasError) {
      return (
        <>
          <LazyImage
            src={ethnicity.leftGirl}
            className={`leftGirlStep1 ${isActive ? 'active' : ''}`}
            alt={ethnicity.alt}
          />
          <LazyImage
            src={ethnicity.image}
            className={`centeredGirlStep1 ${isActive ? 'active' : ''}`}
            alt={ethnicity.alt}
          />
          <LazyImage
            src={ethnicity.rightGirl}
            className={`rightGirlStep1 ${isActive ? 'active' : ''}`}
            alt={ethnicity.alt}
          />
        </>
      );
    }

    return (
      <>
        {/* <LazyImage
          src={ethnicity.leftGirl}
          className={`leftGirlStep1 ${isActive ? 'active' : ''}`}
          alt={ethnicity.alt}
        /> */}

        <video
          ref={(el) => (videoRefs.current[`${ethnicity.value}_extend`] = el)}
          key={`${ethnicity.value}_extend`}
          className={`centeredGirlStep1 ${isActive ? 'active' : 'none'}`}
          {...getVideoProps()}
          preload="auto"
          onMouseEnter={(e) => handleVideoMouseEnter(e, isActive)}
          onMouseLeave={(e) => handleVideoMouseLeave(e, isActive)}
          onClick={handleVideoClick}
          // poster={ethnicity.image}
          onError={() => {
            setVideoErrors((prev) => ({
              ...prev,
              [`${ethnicity.value}_extend`]: true,
            }));
          }}
          style={{
            objectFit: 'cover',
          }}
        />

        <video
          ref={(el) => (videoRefs.current[ethnicity.value] = el)}
          key={ethnicity.value}
          className={`centeredGirlStep1 ${
            !isActive && !isMobile ? 'active' : 'none'
          }`}
          {...getVideoProps()}
          preload="auto"
          // poster={ethnicity.image}
          onMouseEnter={(e) => handleVideoMouseEnter(e, false)}
          onMouseLeave={(e) => handleVideoMouseLeave(e, false)}
          onClick={handleVideoClick}
          onError={() => {
            setVideoErrors((prev) => ({ ...prev, [ethnicity.value]: true }));
          }}
          style={{
            objectFit: 'cover',
          }}
        />

        {/* <LazyImage
          src={ethnicity.rightGirl}
          className={`rightGirlStep1 ${isActive ? 'active' : ''}`}
          alt={ethnicity.alt}
        /> */}
      </>
    );
  };

  const ethnicityTitle =
    isHalloween || isNewDesign ? (
      <>
        {t('choose')}
        <span className="specialText"> {t('ethnicity_text')}</span>
      </>
    ) : (
      t('choose_ethnicity')
    );

  return (
    <div className={`quizContentWrapper ${isTransitioning ? 'no-click' : ''}`}>
      <img
        src={iconHearts}
        alt=""
        className="step1-icon-hearts"
        aria-hidden="true"
      />
      <img
        src={iconHeart}
        alt=""
        className="step1-icon-heart"
        aria-hidden="true"
      />
      <QuizContent className={useCarousel ? 'step4-5-design' : 'step1-design'}>
        {useCarousel ? (
          <VideoCarousel
            items={carouselEthnicities}
            title={ethnicityTitle}
            selectedValue={selectedEthnicity}
            onSelect={setSelectedEthnicity}
            isTransitioning={isTransitioning}
            imageContainerClass="ethnicity-video-container"
            forceShowVideos={true}
          />
        ) : (
          <div className="step1-block">
            <QuizTitle>{ethnicityTitle}</QuizTitle>
            <QuizOptions className="ethnicity-options">
              {ethnicities.map((ethnicity) => {
                const isActive = selectedEthnicity === ethnicity.value;

                return (
                  <QuizOption
                    key={ethnicity.value}
                    value={ethnicity.value}
                    name="ethnicity"
                    onClick={() => handleEthnicityClick(ethnicity.value)}
                    poster={ethnicity.image}
                    className={`ethnicity-option ${
                      isActive
                        ? 'selected'
                        : selectedEthnicity
                        ? 'not-selected'
                        : ''
                    } ${ethnicities.length % 2 === 0 ? 'even' : 'odd'}`}
                  >
                    <div className="ethnicity-image">
                      {renderMedia(ethnicity, isActive)}
                    </div>
                    <div className="image-title">{t(ethnicity.text)}</div>
                  </QuizOption>
                );
              })}
            </QuizOptions>
          </div>
        )}

        {/* Age Block — only for /ndg */}
        {isNewDesign && (
          <div className="step1-block">
            <QuizTitle>{t('choose_age')}</QuizTitle>
            <div className={`age-selector ${useCarousel && 'carousel-age'}`}>
              <div className="age-selector-block">
                <div className="age-slider-container">
                  <div className="age-slider-num">18+</div>
                  <div className="age-slider-wrapper">
                    <input
                      ref={sliderRef}
                      type="range"
                      min="18"
                      max="55"
                      value={selectedAge}
                      onChange={handleAgeChange}
                      className="age-slider"
                      disabled={isTransitioning}
                    />
                    <div
                      className="age-thumb-label"
                      style={{
                        left: `calc(${
                          ((selectedAge - 18) / (55 - 18)) * 100
                        }% + ${
                          (0.5 - (selectedAge - 18) / (55 - 18)) * 48
                        }px)`,
                      }}
                    >
                      {selectedAge}
                    </div>
                  </div>
                  <div className="age-slider-num">55+</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`navigation-wrapper ${
            useCarousel
              ? 'step4-5-navigation-wrapper'
              : 'step1-navigation-wrapper'
          }`}
        >
          <button
            className="previous-button"
            onClick={handlePrevious}
            disabled={isTransitioning}
          >
            {t('previous')}
          </button>
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!canContinue || isTransitioning}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step1;
